import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { emergencyServicesAPI, locationsAPI } from '../../api/client';
import { formatPhoneInput } from '../../lib/validators';
import { INSTITUTION_TYPES, DEPENDENCY_TYPES, CARE_LEVELS } from '../../lib/emergencyConstants';
import { MAPS_KEY } from '../../lib/locationConstants';
import { useJsApiLoader, GoogleMap, Marker } from '@react-google-maps/api';
import { ArrowLeft, Save, AlertTriangle, Navigation, Search } from 'lucide-react';
import { toast } from 'sonner';

const MAP_CONTAINER = { width: '100%', height: '220px' };
const MX = { lat: 19.4326, lng: -99.1332 };

function GeoMap({ lat, lng, onChange }) {
  const { isLoaded } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey: MAPS_KEY });
  const pos = lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : null;
  if (!isLoaded) return <div className="w-full h-48 bg-slate-100 animate-pulse rounded-sm" />;
  return (
    <div className="rounded-sm overflow-hidden border border-slate-200">
      <GoogleMap mapContainerStyle={MAP_CONTAINER} center={pos || MX} zoom={pos ? 15 : 10}
        onClick={e => onChange({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
        options={{ mapTypeControl: false, streetViewControl: false }}>
        {pos && <Marker position={pos} draggable onDragEnd={e => onChange({ lat: e.latLng.lat(), lng: e.latLng.lng() })} />}
      </GoogleMap>
      <p className="text-xs text-slate-400 px-2 py-1 bg-slate-50">Haz clic para fijar la ubicación</p>
    </div>
  );
}

function SectionTitle({ n, children }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-6 h-6 bg-soem-primary text-white rounded-full flex items-center justify-center text-xs font-bold">{n}</div>
      <h3 className="font-bold text-soem-navy">{children}</h3>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

export default function EmergencyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [locSearch, setLocSearch] = useState('');
  const [locResults, setLocResults] = useState([]);

  const [form, setForm] = useState({
    name: '', institution_type: 'hospital', dependency_type: 'publica', care_level: '',
    phone: '', contact_name: '', estimated_response_time: '', estimated_arrival_time: '', distance_km: '',
    address: { street: '', ext_number: '', int_number: '', neighborhood: '', postal_code: '', city: '', state: '' },
    gps_location: { lat: '', lng: '' },
    notes: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm(f => ({ ...f, address: { ...f.address, [k]: v } }));
  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  useEffect(() => {
    if (isEdit) {
      emergencyServicesAPI.get(id).then(r => {
        const s = r.data;
        setForm({
          name: s.name || '', institution_type: s.institution_type || 'hospital',
          dependency_type: s.dependency_type || 'publica', care_level: s.care_level || '',
          phone: s.phone || '', contact_name: s.contact_name || '',
          estimated_response_time: s.estimated_response_time || '',
          estimated_arrival_time: s.estimated_arrival_time || '',
          distance_km: s.distance_km || '',
          address: { street: s.address?.street || '', ext_number: s.address?.ext_number || '',
            int_number: s.address?.int_number || '', neighborhood: s.address?.neighborhood || '',
            postal_code: s.address?.postal_code || '', city: s.address?.city || '', state: s.address?.state || '' },
          gps_location: { lat: s.gps_location?.lat || '', lng: s.gps_location?.lng || '' },
          notes: s.notes || '',
        });
      }).catch(() => toast.error('Error al cargar servicio'));
    }
  }, [id, isEdit]);

  const geocodeAddress = async () => {
    const a = form.address;
    const str = [a.street, a.ext_number, a.neighborhood, a.city, a.state, 'México'].filter(Boolean).join(', ');
    if (!str) { toast.error('Ingresa la dirección primero'); return; }
    setGeocoding(true);
    try {
      const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(str)}&key=${MAPS_KEY}`);
      const data = await res.json();
      if (data.status === 'OK') {
        const { lat, lng } = data.results[0].geometry.location;
        set('gps_location', { lat, lng });
        toast.success('Coordenadas obtenidas');
      } else { toast.error('No se encontraron coordenadas'); }
    } catch { toast.error('Error de geocodificación'); }
    finally { setGeocoding(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, institution_type: form.institution_type,
        dependency_type: form.dependency_type,
        care_level: form.care_level || null,
        phone: form.phone || null, contact_name: form.contact_name || null,
        estimated_response_time: form.estimated_response_time ? parseFloat(form.estimated_response_time) : null,
        estimated_arrival_time: form.estimated_arrival_time ? parseFloat(form.estimated_arrival_time) : null,
        distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
        address: { street: form.address.street, ext_number: form.address.ext_number || null,
          int_number: form.address.int_number || null, neighborhood: form.address.neighborhood || null,
          postal_code: form.address.postal_code || null, city: form.address.city || null, state: form.address.state || null },
        gps_location: form.gps_location.lat && form.gps_location.lng
          ? { lat: parseFloat(form.gps_location.lat), lng: parseFloat(form.gps_location.lng) } : null,
        notes: form.notes || null,
      };
      if (isEdit) { await emergencyServicesAPI.update(id, payload); toast.success('Servicio actualizado'); navigate(`/dashboard/emergency-services/${id}`); }
      else { const r = await emergencyServicesAPI.create(payload); toast.success('Servicio creado'); navigate(`/dashboard/emergency-services/${r.data.id}`); }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg).slice(0, 80));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in" data-testid="emergency-form">
      <div className="flex items-center gap-3">
        <button aria-label="Volver a servicios de emergencia" onClick={() => navigate('/dashboard/emergency-services')} className="soem-btn-secondary p-2"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">{isEdit ? 'Editar Servicio' : 'Nuevo Servicio de Emergencia'}</h1>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* S1: Identificación */}
        <div className="soem-card p-6 space-y-5">
          <SectionTitle n="1">Identificación</SectionTitle>
          <div>
            <label htmlFor="f-nombre-de-la-institución" className="soem-label block mb-1.5">Nombre de la institución <span className="text-red-500">*</span></label>
            <input id="f-nombre-de-la-institución" className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} required data-testid="srv-name" />
          </div>
          <div>
            <label htmlFor="f-tipo-de-institución" className="soem-label block mb-2">Tipo de institución</label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(INSTITUTION_TYPES).map(([k, v]) => (
                <button key={k} type="button" onClick={() => set('institution_type', k)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-sm border-2 font-semibold text-sm transition-all ${form.institution_type === k ? `${v.activeBg} text-white border-transparent` : `border-slate-200 ${v.text} hover:border-slate-300`}`}>
                  <span className="text-3xl">{v.emoji}</span>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor="f-tipo-de-dependencia" className="soem-label block mb-1.5">Tipo de dependencia</label>
            <select id="f-tipo-de-dependencia" className={inputCls} value={form.dependency_type} onChange={e => set('dependency_type', e.target.value)}>
              {Object.entries(DEPENDENCY_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          {form.institution_type === 'hospital' && (
            <div>
              <label htmlFor="f-nivel-de-atención" className="soem-label block mb-2">Nivel de atención</label>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(CARE_LEVELS).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => set('care_level', form.care_level === k ? '' : k)}
                    className={`p-3 rounded-sm border-2 text-left transition-all ${form.care_level === k ? 'border-soem-primary bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <p className="font-bold text-sm text-soem-navy">{v.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{v.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* S2: Contacto */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="2">Contacto</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="f-teléfono" className="soem-label block mb-1.5">Teléfono</label>
              <input id="f-teléfono" 
                type="tel" 
                className={inputCls} 
                value={form.phone} 
                onChange={e => set('phone', formatPhoneInput(e.target.value))} 
                placeholder="10 dígitos" 
                maxLength={10}
              />
            </div>
            <div>
              <label htmlFor="f-nombre-del-contacto" className="soem-label block mb-1.5">Nombre del contacto</label>
              <input id="f-nombre-del-contacto" className={inputCls} value={form.contact_name} onChange={e => set('contact_name', e.target.value)} />
            </div>
          </div>
        </div>

        {/* S3: Tiempos */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="3">Tiempos y Distancia</SectionTitle>
          <div className="grid grid-cols-3 gap-4">
            {[
              { k: 'estimated_response_time', l: 'Tiempo de respuesta', u: 'min' },
              { k: 'estimated_arrival_time', l: 'Tiempo de llegada', u: 'min' },
              { k: 'distance_km', l: 'Distancia', u: 'km' },
            ].map(({ k, l, u }) => (
              <div key={k}>
                <label htmlFor={`f-${k}`} className="soem-label block mb-1.5">{l}</label>
                <div className="relative">
                  <input id={`f-${k}`} type="number" min="0" className={inputCls} value={form[k]}
                    onChange={e => set(k, e.target.value)} placeholder="0" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{u}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* S4: Dirección y GPS */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="4">Dirección y GPS</SectionTitle>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label htmlFor="f-calle" className="soem-label block mb-1.5">Calle</label>
              <input id="f-calle" className={inputCls} value={form.address.street} onChange={e => setAddr('street', e.target.value)} />
            </div>
            <div>
              <label htmlFor="f-no-ext" className="soem-label block mb-1.5">No. Ext</label>
              <input id="f-no-ext" className={inputCls} value={form.address.ext_number} onChange={e => setAddr('ext_number', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="f-colonia" className="soem-label block mb-1.5">Colonia</label>
              <input id="f-colonia" className={inputCls} value={form.address.neighborhood} onChange={e => setAddr('neighborhood', e.target.value)} />
            </div>
            <div>
              <label htmlFor="f-ciudad" className="soem-label block mb-1.5">Ciudad</label>
              <input id="f-ciudad" className={inputCls} value={form.address.city} onChange={e => setAddr('city', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="f-latitud" className="soem-label block mb-1.5">Latitud</label>
              <input id="f-latitud" type="number" step="any" className={inputCls} value={form.gps_location.lat}
                placeholder="19.4326"
                onChange={e => set('gps_location', { ...form.gps_location, lat: e.target.value })} />
            </div>
            <div>
              <label htmlFor="f-longitud" className="soem-label block mb-1.5">Longitud</label>
              <input id="f-longitud" type="number" step="any" className={inputCls} value={form.gps_location.lng}
                placeholder="-99.1332"
                onChange={e => set('gps_location', { ...form.gps_location, lng: e.target.value })} />
            </div>
          </div>
          <button type="button" onClick={geocodeAddress} disabled={geocoding}
            className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            <Navigation size={14} /> {geocoding ? 'Obteniendo...' : 'Obtener coordenadas'}
          </button>
          <GeoMap lat={form.gps_location.lat} lng={form.gps_location.lng}
            onChange={({ lat, lng }) => set('gps_location', { lat, lng })} />
        </div>

        {/* S5: Notas */}
        <div className="soem-card p-6">
          <SectionTitle n="5">Notas</SectionTitle>
          <textarea className={`${inputCls} resize-none`} rows={3} value={form.notes}
            onChange={e => set('notes', e.target.value)} placeholder="Información adicional..." />
        </div>

        <div className="flex gap-4">
          <button type="submit" disabled={saving}
            className="soem-btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-service-btn">
            <Save size={15} /> {saving ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Guardar Servicio')}
          </button>
          <button type="button" onClick={() => navigate('/dashboard/emergency-services')}
            className="soem-btn-secondary px-8 py-3 text-sm">Cancelar</button>
        </div>
      </form>
    </div>
  );
}
